"use client";

import { useState } from "react";
import {
  DataTable,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  AbsolutePanel,
  CombinePanel,
  DistancePanel,
  GainPanel,
  SoundLevelPanel,
} from "@/components/tools/physics-waves/decibel-panels";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const DECIBEL_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modes: {
    level: "Sound level, intensity and pressure",
    combine: "Add or subtract levels",
    distance: "Distance from the source",
    gain: "Power and voltage ratios",
    absolute: "Absolute levels (dBm, dBV)",
  },
  placesLabel: "Decimal places for decibels",
  results: "Results",
  working: "The working",
  unitFor: "Unit for {name}",
  enterValue: "Enter a valid number in every box to see the result.",
  enterPositive:
    "Enter a number above zero (a ratio or a power cannot be zero or negative).",
  knownLabel: "What do you know?",
  knowns: {
    level: "The level in dB",
    intensity: "The intensity",
    pressure: "The sound pressure",
  },
  levelInput: "Sound level (dB)",
  intensityInput: "Sound intensity",
  pressureInput: "Sound pressure (RMS)",
  levelResult: "Sound level (L)",
  intensityResult: "Sound intensity (I)",
  pressureResult: "Sound pressure (p)",
  ratioResult: "Intensity ÷ threshold of hearing",
  stepIntensity: "I = I₀ × 10^(L/10) = 10⁻¹² W/m² × 10^({level}/10) = {i}",
  stepPressure: "p = p₀ × 10^(L/20) = 20 µPa × 10^({level}/20) = {p}",
  exposureNote:
    "At {level} (if A-weighted), NIOSH's 3 dB rule limits exposure to about {time} a day; its recommended limit is 85 dBA for 8 hours.",
  hoursValue: "{value} h",
  minutesValue: "{value} min",
  secondsValue: "{value} s",
  combineLabel: "What do you want to do?",
  combines: {
    add: "Add several sources",
    subtract: "Remove background noise",
    identical: "Identical sources",
  },
  levelRow: "Source {n} level",
  removeRow: "Remove source {n}",
  addRow: "Add a source",
  enterTwoLevels: "Enter at least one level to see the total.",
  totalLevel: "Combined level",
  aboveLoudest: "Above the loudest source",
  aboveSingle: "Above a single source",
  stepAdd: "L = 10 × log₁₀({terms}) = {total}",
  measuredTotal: "Measured total (source plus background)",
  backgroundLevel: "Background level alone",
  singleLevel: "Level of one source",
  sourceCount: "Number of identical sources",
  backgroundTooLoud:
    "The background must be quieter than the measured total; otherwise nothing is left that can be measured.",
  sourceLevel: "Level of the source alone",
  correction: "Correction to the total",
  stepSubtract:
    "L = 10 × log₁₀(10^({total}/10) − 10^({background}/10)) = {source}",
  stepIdentical: "L = {level} + 10 × log₁₀({n}) = {total}",
  distanceGoalLabel: "What do you want to find?",
  goalLevel: "The level at a new distance",
  goalDistance: "The distance for a target level",
  knownLevel: "Level at the known distance (dB)",
  knownDistance: "Known distance",
  newDistance: "New distance",
  targetLevel: "Target level (dB)",
  levelAtDistance: "Level at the new distance",
  distanceForLevel: "Distance for that level",
  levelChange: "Change from the known level",
  perDoubling: "Drop for each doubling of distance",
  stepDistance:
    "L₂ = L₁ − 20 × log₁₀(r₂ ÷ r₁): from {l1} at {r1} to {l2} at {r2}",
  falloffHeading: "Level at multiples of the known distance",
  columnDistance: "Distance",
  columnLevel: "Level",
  freeFieldNote:
    "Assumes a point source radiating in an open space (a free field), where the intensity falls with the square of the distance. Indoors, reflections reduce the fall; a long line source loses only 3 dB per doubling.",
  ratioKindLabel: "The ratio is of",
  kindPower: "Power (10 log)",
  kindAmplitude: "Voltage or pressure (20 log)",
  directionLabel: "Convert",
  directionToDb: "Ratio to decibels",
  directionToRatio: "Decibels to ratio",
  ratioInput: "Ratio (output ÷ input)",
  gainInput: "Gain in decibels",
  gainResult: "Gain",
  ratioResult2: "Ratio",
  percentChange: "Change",
  stepGain: "dB = {factor} × log₁₀(ratio), so a ratio of {ratio} is {db}",
  absoluteKindLabel: "Which quantity?",
  absolutePower: "Power",
  absoluteVoltage: "Voltage",
  absoluteNote:
    "dBm is relative to 1 mW, dBW to 1 W, dBV to 1 V RMS and dBu to 0.7746 V RMS (the voltage that puts 1 mW into 600 Ω).",
  examplesHeading: "Typical sound levels",
  columnSound: "Sound",
  columnTypical: "Level",
  examples: {
    threshold: "Threshold of hearing at 1 kHz",
    whisper: "A whisper",
    conversation: "Normal conversation",
    tools: "Lawn mower, vacuum cleaner, power tools",
    siren: "Ambulance siren, chain saw, nightclub",
    pain: "Threshold of pain",
  },
  exampleLevels: {
    threshold: "0 dB",
    whisper: "about 30 dB",
    conversation: "about 60 dB",
    tools: "85–90 dBA",
    siren: "95 dBA and above",
    pain: "about 120–130 dB",
  },
  examplesNote:
    "Rounded typical values; the 85 dBA limit and the 85–90 and 95-plus examples come from NIOSH. A-weighting (dBA) adjusts a measurement to the ear's sensitivity, which this calculator does not do.",
} as const;

type Mode = keyof typeof DECIBEL_CALCULATOR_STRINGS.modes;

const PLACE_CHOICES = ["0", "1", "2", "3"] as const;
type ExampleKey = keyof typeof DECIBEL_CALCULATOR_STRINGS.examples;

export default function DecibelCalculatorWidget() {
  const strings = useToolUi(DECIBEL_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("level");
  const [placesText, setPlacesText] =
    useState<(typeof PLACE_CHOICES)[number]>("1");
  const places = Number(placesText);
  const panel = { strings, fmt, places };
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="db-mode"
        onChange={setMode}
        options={(Object.keys(strings.modes) as Mode[]).map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={mode}
      />
      <div className="max-w-xs">
        <SelectField
          id="db-places"
          label={strings.placesLabel}
          onChange={setPlacesText}
          options={PLACE_CHOICES.map((value) => ({ value, label: value }))}
          value={placesText}
        />
      </div>
      {mode === "level" ? <SoundLevelPanel {...panel} /> : null}
      {mode === "combine" ? <CombinePanel {...panel} /> : null}
      {mode === "distance" ? <DistancePanel {...panel} /> : null}
      {mode === "gain" ? <GainPanel {...panel} /> : null}
      {mode === "absolute" ? <AbsolutePanel {...panel} /> : null}
      <div className="space-y-3">
        <h2 className={WIDGET_H3}>{strings.examplesHeading}</h2>
        <DataTable
          head={[strings.columnSound, strings.columnTypical]}
          rows={(Object.keys(strings.examples) as ExampleKey[]).map((key) => [
            strings.examples[key],
            strings.exampleLevels[key],
          ])}
        />
        <p className="text-muted-foreground text-xs">{strings.examplesNote}</p>
      </div>
    </>
  );
}
